import cx from "classnames";
import { EditorState } from "draft-js";
import { ReactElement, useMemo } from "react";

import { Box, IconButton, MenuItem, SvgIcon } from "@material-ui/core";
import {
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatBold,
  FormatItalic,
  FormatUnderlined
} from "@material-ui/icons";

import Select from "src/components/form/Select";
import { useFormInputStyles } from "src/styles/forms";
import palette from "src/styles/palette";

export const BLOCK = "block";
export const INLINE = "inline";
export type AllowedCommand = typeof BLOCK | typeof INLINE;

const ACTIONS: {
  style: string;
  Icon: typeof SvgIcon;
  type: AllowedCommand;
}[] = [
  {
    style: "BOLD",
    Icon: FormatBold,
    type: INLINE
  },
  {
    style: "ITALIC",
    Icon: FormatItalic,
    type: INLINE
  },
  {
    style: "UNDERLINE",
    Icon: FormatUnderlined,
    type: INLINE
  },
  {
    style: "blockquote",
    Icon: FormatQuote,
    type: BLOCK
  },
  {
    style: "unordered-list-item",
    Icon: FormatListBulleted,
    type: BLOCK
  },
  {
    style: "ordered-list-item",
    Icon: FormatListNumbered,
    type: BLOCK
  }
];

const HEADER_ACTIONS = [
  { label: "H1", style: "header-one" },
  { label: "H2", style: "header-two" },
  { label: "H3", style: "header-three" }
];

export const Toolbar = ({
  editorState,
  onCommandClick
}: {
  editorState: EditorState;
  onCommandClick: (command: string, type: AllowedCommand) => void;
}): ReactElement => {
  const classes = useFormInputStyles();

  const selection = editorState.getSelection();
  const activeStyle = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  const isHeaderActive = useMemo(() => {
    return HEADER_ACTIONS.some(({ style }) => style === activeStyle);
  }, [activeStyle]);

  return (
    <Box className={classes.toolbar}>
      <Select
        className={classes.selectWithSpacing}
        variant="underlined"
        value={isHeaderActive ? activeStyle : "body"}
        onChange={e => onCommandClick(e?.target?.value, BLOCK)}
        renderValue={style => {
          const { label } =
            HEADER_ACTIONS.find(header => header.style === style) || {};
          return (
            <Box sx={{ padding: "0 8px", width: "30px" }}>
              {label || "Body"}
            </Box>
          );
        }}
      >
        <MenuItem value="body">Body</MenuItem>
        {HEADER_ACTIONS.map(({ label, style }) => (
          <MenuItem key={style} value={style}>
            {label}
          </MenuItem>
        ))}
      </Select>

      <Box className={classes.toolbarButtons}>
        {ACTIONS.map(action => {
          const { Icon, style, type } = action;
          const selection = editorState.getSelection();
          const isActive =
            type === INLINE
              ? editorState.getCurrentInlineStyle().has(style)
              : editorState
                  .getCurrentContent()
                  .getBlockForKey(selection.getStartKey())
                  .getType() === style;

          return (
            <IconButton
              aria-label={style}
              className={cx(classes.toolbarButton, {
                [classes.toolbarButtonActive]: isActive
              })}
              key={style}
              onMouseDown={e => {
                // Discussion of managing focus and why we have to use onMouseDown:
                // https://github.com/facebookarchive/draft-js/issues/696
                e.preventDefault();
                onCommandClick(style, type);
              }}
            >
              <Icon style={{ color: isActive ? "#fff" : palette.darkGray }} />
            </IconButton>
          );
        })}
      </Box>
    </Box>
  );
};
